import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/354b6cde-f378-42cf-a9dc-2356d64fa0ef/317478c1-082b-4ae3-9103-21d92ed78b63_Calm-Authority-1200-x-400-Transparent.svg?v=128148ac72e991491086eb8d499051ad';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation('common');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/', label: t('nav_home') },
    { to: '/quiz', label: t('nav_quiz') },
    { to: '/decision-tree', label: t('nav_decision_tree') },
    { to: '/about', label: t('nav_about') },
    { to: '/contact', label: t('nav_contact') },
    { to: '/corporate-enquiries', label: 'Corporate Enquiries' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const scrollToTop = (targetPath?: string) => {
    window.setTimeout(() => {
      if (targetPath?.includes('#')) {
        const hash = targetPath.split('#')[1];
        const target = document.getElementById(hash);
        if (target) {
          target.scrollIntoView({ behavior: "auto", block: "start" });
          return;
        }
      }
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 0);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20 md:h-28 lg:h-32">
          {/* Logo */}
          <Link to="/" onClick={() => scrollToTop("/")} className="flex-shrink-0 flex items-center">
            <img
              src={LOGO_URL}
              alt="Calm Authority"
              className="h-12 md:h-20 lg:h-24 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => scrollToTop(link.to)}
                className={`px-3 lg:px-4 py-2 text-sm lg:text-base font-medium rounded-lg transition-colors whitespace-nowrap ${
                  isActive(link.to)
                    ? 'text-teal'
                    : 'text-navy hover:text-teal'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side: CTA */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            <Link
              to="/quiz"
              onClick={() => scrollToTop("/quiz")}
              className="bg-teal text-white text-sm lg:text-base font-semibold px-5 lg:px-6 py-2.5 lg:py-3 rounded-lg hover:bg-navy transition-colors whitespace-nowrap"
            >
              {t('nav_cta')}
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 text-navy"
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-seafoam/30 shadow-lg">
          <nav className="flex flex-col px-4 py-4 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => scrollToTop(link.to)}
                className={`px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                  isActive(link.to)
                    ? 'text-teal bg-seafoam/20'
                    : 'text-navy hover:bg-seafoam/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/quiz"
              onClick={() => scrollToTop("/quiz")}
              className="mt-2 bg-teal text-white text-base font-semibold px-4 py-3 rounded-lg text-center"
            >
              {t('nav_cta')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
